import { atom, computed } from 'nanostores';
import { userConfigSchema, type HomeLayout, type UserConfig } from '../../common/schema/user-config.schema';

export {
  HOME_SECTIONS,
  userConfigSchema,
  type HomeLayout,
  type HomeSection,
  type UserConfig,
} from '../../common/schema/user-config.schema';

export type Theme = 'dark' | 'light';
export type ThemeMode = 'system' | Theme;

const THEME_KEY = 'bgmer.theme';
const systemThemeQuery = window.matchMedia('(prefers-color-scheme: light)');
const savedThemeMode = localStorage.getItem(THEME_KEY);
const initialThemeMode: ThemeMode =
  savedThemeMode === 'dark' || savedThemeMode === 'light' || savedThemeMode === 'system' ? savedThemeMode : 'system';
const resolveTheme = (mode: ThemeMode): Theme =>
  mode === 'system' ? (systemThemeQuery.matches ? 'light' : 'dark') : mode;
type PersistConfig = (config: UserConfig) => Promise<void>;
const USER_CONFIG_DEBOUNCE_MS = 200;
const AI_SELECTION_PROMPT_DEBOUNCE_MS = 800;

const defaultConfig = (): UserConfig => userConfigSchema.parse({});
export const $userConfig = atom<UserConfig>(defaultConfig());

export const $themeMode = atom<ThemeMode>(initialThemeMode);
export const $theme = atom<Theme>(resolveTheme(initialThemeMode));
export const $showNsfw = computed($userConfig, (config) => config.showNsfw);
export const $japaneseAnimeOnly = computed($userConfig, (config) => config.japaneseAnimeOnly);
export const $showLateNightAnime = computed($userConfig, (config) => config.showLateNightAnime);
export const $scheduleFollowingOnly = computed($userConfig, (config) => config.scheduleFollowingOnly);
export const $useAiMediaSelection = computed($userConfig, (config) => config.useAiMediaSelection);
export const $aiSelectionPrompt = computed($userConfig, (config) => config.aiSelectionPrompt);
export const $homeLayout = computed($userConfig, (config) => config.homeLayout);

let persistConfig: PersistConfig | undefined;
let handlePersistError: ((error: unknown) => void) | undefined;
const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

const clearDebounceTimers = (): void => {
  for (const timer of debounceTimers.values()) {
    clearTimeout(timer);
  }
  debounceTimers.clear();
};

const schedulePersist = (delay = USER_CONFIG_DEBOUNCE_MS, key = 'config'): void => {
  const persist = persistConfig;
  if (!persist) {
    return;
  }
  const currentTimer = debounceTimers.get(key);
  if (currentTimer) {
    clearTimeout(currentTimer);
  }
  debounceTimers.set(
    key,
    setTimeout(() => {
      debounceTimers.delete(key);
      void persist($userConfig.get()).catch((error: unknown) => handlePersistError?.(error));
    }, delay),
  );
};

const updateConfig = <Key extends keyof UserConfig>(
  key: Key,
  value: UserConfig[Key],
  debounceMs = USER_CONFIG_DEBOUNCE_MS,
  debounceKey = 'config',
): void => {
  const config = userConfigSchema.parse({ ...$userConfig.get(), [key]: value });
  $userConfig.set(config);
  schedulePersist(debounceMs, debounceKey);
};

export const hydrateUserConfig = (value: unknown): UserConfig => {
  const result = userConfigSchema.safeParse(value);
  const config = result.success ? result.data : defaultConfig();
  $userConfig.set(config);
  return config;
};

export const configureUserConfigPersistence = (
  persist: PersistConfig,
  onError?: (error: unknown) => void,
): (() => void) => {
  persistConfig = persist;
  handlePersistError = onError;
  return () => {
    if (persistConfig === persist) {
      clearDebounceTimers();
      persistConfig = undefined;
      handlePersistError = undefined;
    }
  };
};

export const resetUserConfig = (): void => {
  clearDebounceTimers();
  $userConfig.set(defaultConfig());
};

systemThemeQuery.addEventListener('change', () => {
  if ($themeMode.get() === 'system') {
    $theme.set(resolveTheme('system'));
  }
});

export const setThemeMode = (value: ThemeMode): void => {
  $themeMode.set(value);
  $theme.set(resolveTheme(value));
  localStorage.setItem(THEME_KEY, value);
};
export const setShowNsfw = (value: boolean): void => updateConfig('showNsfw', value);
export const setJapaneseAnimeOnly = (value: boolean): void => updateConfig('japaneseAnimeOnly', value);
export const setShowLateNightAnime = (value: boolean): void => updateConfig('showLateNightAnime', value);
export const setScheduleFollowingOnly = (value: boolean): void => updateConfig('scheduleFollowingOnly', value);
export const setUseAiMediaSelection = (value: boolean): void => updateConfig('useAiMediaSelection', value);
export const setAiSelectionPrompt = (value: string): void =>
  updateConfig('aiSelectionPrompt', value, AI_SELECTION_PROMPT_DEBOUNCE_MS, 'aiSelectionPrompt');
export const setHomeLayout = (value: HomeLayout): void => updateConfig('homeLayout', value);
